import { useAuthedRest } from '@/api/useAuthedRest';

import { useGraphQLQuery } from './useGraphQLQuery';

// Mirrors StagedListing from the API
// (apps/api-cf/src/backstage/sourcing/crawler/crawler.service.ts). `firstSeenAt` arrives as an ISO
// string over JSON. The two flags are derived server-side from lifecycle
// columns, and `classification` is recomputed there from the captured feed
// payload — the crawl never stores its verdict.
export interface StagedListing {
  id: string;
  title: string;
  url: string;
  handle: string;
  skuRoot: string | null;
  imageUrl: string | null;
  available: boolean;
  description: string;
  firstSeenAt: string;
  platform: string;
  hasExtraction: boolean;
  // The page-extracted description copy, when the crawl stored one (null =
  // feed-only). See StagedListing in apps/api-cf/src/backstage/sourcing/crawler/crawler.service.ts.
  extractedDescription: string | null;
  productType: string | null;
  categorySlugs: string[];
  classification: 'coffee' | 'ambiguous' | 'excluded';
  newCrop: boolean;
  delisted: boolean;
}

interface UseRoasterListingsReturn {
  listings: StagedListing[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<StagedListing[] | null>;
}

// Staged listings for one roaster (read-only review). Reuses the
// transport-agnostic useGraphQLQuery machinery over the REST admin endpoint,
// exactly like useCrawlRuns. Disabled until a roasterId is known.
export function useRoasterListings(roasterId: string | undefined): UseRoasterListingsReturn {
  const rest = useAuthedRest();

  const { data, isLoading, error, refetch } = useGraphQLQuery<StagedListing[]>({
    // `enabled` gates the fetch, so roasterId is always set when this runs; the
    // `?? ''` only satisfies the type-checker.
    fetcher: (signal) =>
      rest.request<StagedListing[]>(`/admin/crawl/${roasterId ?? ''}/listings`, { signal }),
    deps: [roasterId],
    enabled: Boolean(roasterId),
  });

  return { listings: data, isLoading, error, refetch };
}
